import * as Popper from "@popperjs/core";
import debounce from "../debounce/module.debounce.js";
import { sanitize } from "@/helpers/sanitize.helper.js";

// ------------------------
// Global Configuration
// ------------------------
// defaultPlacement: initial popper placement before any flips
// defaultTrigger: choose 'hover' for desktop, 'click' for touch by default
// defaultMinWidth / defaultMaxWidth: CSS units for tooltip sizing
// edgePadding: padding in preventOverflow modifier
// isInteractive: if true, tooltip stays open when hovered
// fallbackPlacements: order of placements for Popper's flip modifier
// transitionDurationIn/Out: animation durations for show/hide (ms)
// delay: hover show delay (ms)
const TOOLTIP_CONFIG = {
  defaultPlacement: "top",
  defaultTrigger: { desktop: "hover", touch: "click" },
  defaultMinWidth: "0px",
  defaultMaxWidth: "575px",
  edgePadding: { top: 300, bottom: 10, right: 10, left: 10 },
  isInteractive: true,
  fallbackPlacements: ["top", "bottom"],
  transitionDurationIn: 300,
  transitionDurationOut: 200,
  delay: 100
};

// Shortcut map for fallback placements
const PLACEMENT_MAP = {
  none: [],
  vertical: ["top", "bottom"],
  horizontal: ["left", "right"]
};

// ------------------------
// Input Mode Detection
// ------------------------
let currentInputMode = detectInputMode();

/**
 * Returns 'touch' if device supports touch events, otherwise 'desktop'.
 */
function detectInputMode() {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0
    ? "touch"
    : "desktop";
}

// Update input mode on window resize (e.g. devtools toggles)
const handleResize = debounce(() => {
  const newMode = detectInputMode();
  if (newMode !== currentInputMode) {
    currentInputMode = newMode;
  }
}, 200);
window.addEventListener("resize", handleResize);
// Also update on real pointer interactions
window.addEventListener("pointerdown", (e) => {
  currentInputMode = e.pointerType === "touch" ? "touch" : "desktop";
});

// ------------------------
// Global Tooltip Management
// ------------------------
// Set of active controllers to close on outside click
const controllers = new Set();

document.addEventListener("pointerdown", (e) => {
  controllers.forEach((ctrl) => {
    if (
      ctrl.isVisible() &&
      !ctrl.el.contains(e.target) &&
      !ctrl.tooltip.contains(e.target)
    ) {
      ctrl.hide();
    }
  });
});

// ------------------------
// TooltipController Class
// ------------------------
class TooltipController {
  constructor(el, config) {
    this.el = el; // trigger element
    this.config = config; // parsed per-element config
    this.isTouchHandled = false; // flag to suppress duplicate click after touch
    this.showTimeout = null; // hover-show timer handle
    this.hideTimeout = null; // hover-hide timer handle
    this.isIdVariant = Boolean(config.tooltipId);

    this.createTooltipElement();
    this.createPopperInstance();
    this.updateMaxWidth();
    this.bindEvents();

    // Update maxWidth on window resize
    this.resizeHandler = () => this.updateMaxWidth();
    window.addEventListener("resize", this.resizeHandler);

    controllers.add(this); // register for outside-click closing
  }

  /** Update maxWidth based on viewport and padding */
  updateMaxWidth() {
    const maxWidthValue = parseInt(this.config.maxWidth);
    const viewportWidth = window.innerWidth;
    const padding = this.config.edgePadding;
    const availableWidth = viewportWidth - padding.left - padding.right;
    const constrainedWidth = Math.min(maxWidthValue, availableWidth);
    this.tooltip.style.maxWidth = `${constrainedWidth}px`;
  }

  /** Build tooltip DOM structure and insert/defer insertion */
  createTooltipElement() {
    const { tooltipContent, tooltipId, minWidth, maxWidth } = this.config;
    // wrapper inner for animation/content
    const inner = document.createElement("div");
    inner.className = "c-tooltip__inner";
    this.contentEl = document.createElement("span");
    inner.appendChild(this.contentEl);

    // tooltip container
    this.tooltip = document.createElement("div");
    this.tooltip.className = "c-tooltip";

    if (tooltipContent != null) {
      // inline variant: initial content
      this.contentEl.innerHTML = sanitize(tooltipContent);
      this.el.after(this.tooltip);
    } else if (tooltipId) {
      // ID variant: defer insertion, load content
      const tpl = document.getElementById(tooltipId);
      if (!tpl) console.error(`Tooltip ID ${tooltipId} not found`);
      this.contentEl.innerHTML = sanitize(tpl?.innerHTML);
      Array.from(tpl?.attributes || []).forEach((a) =>
        this.tooltip.setAttribute(a.name, a.value)
      );
    } else {
      // nextSibling variant: reuse existing node
      const sib = this.el.nextElementSibling;
      this.contentEl.innerHTML = sanitize(sib?.innerHTML);
      sib.innerHTML = "";
      this.tooltip = sib;
    }

    // close button
    const btn = document.createElement("button");
    btn.className = "c-tooltip__close-icon";
    btn.setAttribute("aria-label", "Sluiten");
    btn.addEventListener("click", () => this.hide());
    inner.prepend(btn);

    // arrow
    this.arrow = document.createElement("div");
    this.arrow.className = "c-tooltip__arrow";
    inner.appendChild(this.arrow);

    // append inner
    this.tooltip.appendChild(inner);
    // sizing
    this.tooltip.style.minWidth = minWidth;
    this.tooltip.style.maxWidth = maxWidth;
    this.inner = inner;
  }

  /** Initialize Popper with modifiers */
  createPopperInstance() {
    const { placement, edgePadding, fallbackPlacements } = this.config;
    this.popper = Popper.createPopper(this.el, this.tooltip, {
      placement,
      strategy: "fixed",
      modifiers: [
        {
          name: "arrow",
          options: { element: this.arrow, scroll: false, resize: false }
        },
        {
          name: "preventOverflow",
          options: {
            boundary: "viewport",
            padding: edgePadding,
            scroll: false,
            resize: false
          }
        },
        {
          name: "flip",
          options: {
            boundary: "viewport",
            fallbackPlacements,
            scroll: false,
            resize: false
          }
        }
      ]
    });
  }

  /** Determine 'hover' vs 'click' trigger */
  getTrigger() {
    return (
      this.config.triggerOverride ||
      TOOLTIP_CONFIG.defaultTrigger[currentInputMode]
    );
  }

  /** Whether tooltip is shown */
  isVisible() {
    return this.tooltip.hasAttribute("data-show");
  }

  /** Update content live for inline, nextSibling, and ID variants */
  updateContent() {
    if (this.config.tooltipContent != null) {
      const updated = this.el.dataset.tooltipContent || "";
      if (this.contentEl.innerHTML !== sanitize(updated))
        this.contentEl.innerHTML = sanitize(updated);
    } else if (this.isIdVariant) {
      const tpl = document.getElementById(this.config.tooltipId);
      if (tpl) this.contentEl.innerHTML = sanitize(tpl.innerHTML);
    }
  }

  /** Show tooltip, reapply flip, animate in */
  show() {
    // ensure content fresh
    this.updateContent();
    // append ID variant
    if (this.isIdVariant && this.tooltip.parentNode !== document.body)
      document.body.appendChild(this.tooltip);
    // prepare for measurement
    this.tooltip.style.visibility = "hidden";
    this.tooltip.style.display = "block";

    // reapply flip with fallback
    this.popper.setOptions((opts) => {
      const mods = opts.modifiers.map((m) =>
        m.name === "flip"
          ? {
              ...m,
              options: {
                ...m.options,
                fallbackPlacements: this.config.fallbackPlacements,
                padding: TOOLTIP_CONFIG.edgePadding
              }
            }
          : m
      );
      return { ...opts, modifiers: mods };
    });
    this.popper.update();
    // animate
    this.inner.style.transitionDuration = `${this.config.transitionDurationIn}ms`;
    this.tooltip.setAttribute("data-show", "");
    this.tooltip.hidden = false;
    this.tooltip.style.visibility = "visible";
    this.el.setAttribute("aria-expanded", "true");
  }

  /** Hide tooltip, animate out, cleanup ID */
  hide() {
    this.inner.style.transitionDuration = `${this.config.transitionDurationOut}ms`;
    this.tooltip.removeAttribute("data-show");
    this.tooltip.removeAttribute("data-show-close-button");
    this.el.setAttribute("aria-expanded", "false");
    setTimeout(() => {
      this.tooltip.style.visibility = "";
      this.tooltip.style.display = "none";
      if (this.isIdVariant && this.tooltip.parentNode === document.body)
        document.body.removeChild(this.tooltip);
    }, this.config.transitionDurationOut);
  }

  /** Toggle show/hide */
  toggle(e) {
    e.stopPropagation();
    this.isVisible() ? this.hide() : this.show();
  }

  /** Bind hover and click event handlers */
  bindEvents() {
    const { delay, isInteractive } = this.config;
    const clearShow = () => {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    };
    const clearHide = () => {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    };

    const onEnter = (e) => {
      if (this.getTrigger() !== "hover") return;
      clearHide();
      this.showTimeout = setTimeout(() => this.show(), delay);
    };
    const onLeave = (e) => {
      if (this.getTrigger() !== "hover") return;
      clearShow();
      if (isInteractive) {
        this.hideTimeout = setTimeout(() => {
          if (!this.tooltip.matches(":hover")) this.hide();
        }, 50);
      } else this.hide();
    };
    const onTooltipEnter = (e) => {
      if (this.getTrigger() !== "hover") return;
      clearHide();
    };
    const onTooltipLeave = (e) => {
      if (this.getTrigger() !== "hover") return;
      this.hideTimeout = setTimeout(() => {
        if (!this.el.matches(":hover")) this.hide();
      }, 50);
    };

    const onTouch = (e) => {
      if (this.getTrigger() !== "click") return;
      this.isTouchHandled = true;
      this.tooltip.setAttribute("data-show-close-button", "");
      this.toggle(e);
      e.preventDefault();
    };
    const onClick = (e) => {
      if (this.getTrigger() !== "click") return;
      if (currentInputMode === "touch" && this.isTouchHandled) {
        this.isTouchHandled = false;
        return;
      }
      this.toggle(e);
    };

    this.el.addEventListener("mouseenter", onEnter);
    this.el.addEventListener("focus", onEnter);
    this.el.addEventListener("mouseleave", onLeave);
    this.el.addEventListener("blur", onLeave);
    this.el.addEventListener("touchstart", onTouch, { passive: false });
    this.el.addEventListener("click", onClick);
    if (isInteractive) {
      this.tooltip.addEventListener("mouseenter", onTooltipEnter);
      this.tooltip.addEventListener("mouseleave", onTooltipLeave);
    }
    this.handlers = {
      onEnter,
      onLeave,
      onTooltipEnter,
      onTooltipLeave,
      onTouch,
      onClick
    };
  }

  /** Cleanup instance */
  destroy() {
    window.removeEventListener("resize", this.resizeHandler);
    this.popper.destroy();
    Object.entries(this.handlers).forEach(([n, fn]) => {
      const e = n.replace(/^on/, "").toLowerCase();
      this.el.removeEventListener(e, fn);
      if (this.tooltip) this.tooltip.removeEventListener(e, fn);
    });
    if (this.tooltip.parentNode)
      this.tooltip.parentNode.removeChild(this.tooltip);
    controllers.delete(this);
  }
}

// ------------------------
// Initialization Logic
// ------------------------
const initializedElements = new WeakSet();
function initTooltips(scope = document) {
  scope.querySelectorAll(".js-tooltip").forEach((el) => {
    if (initializedElements.has(el)) return;
    initializedElements.add(el);

    // get all the data attributes or get them from the config
    const cfg = {
      triggerOverride: el.dataset.tooltipTrigger,
      tooltipContent: el.dataset.tooltipContent,
      tooltipId: el.dataset.tooltipId,
      placement: el.dataset.tooltipPlacement || TOOLTIP_CONFIG.defaultPlacement,
      fallbackPlacements: (() => {
        const f = el.dataset.tooltipPlacementFallbacks;
        return f in PLACEMENT_MAP
          ? PLACEMENT_MAP[f]
          : f
            ? f.split(",").map((x) => x.trim())
            : TOOLTIP_CONFIG.fallbackPlacements;
      })(),
      minWidth:
        el.dataset.tooltipMinWidth &&
        /^[0-9]+$/.test(el.dataset.tooltipMinWidth)
          ? `${el.dataset.tooltipMinWidth}px`
          : el.dataset.tooltipMinWidth || TOOLTIP_CONFIG.defaultMinWidth,
      maxWidth:
        el.dataset.tooltipMaxWidth &&
        /^[0-9]+$/.test(el.dataset.tooltipMaxWidth)
          ? `${el.dataset.tooltipMaxWidth}px`
          : el.dataset.tooltipMaxWidth || TOOLTIP_CONFIG.defaultMaxWidth,
      edgePadding: (() => {
        if (el.dataset.tooltipEdgePadding) {
          const attr = el.dataset.tooltipEdgePadding;
          // Try to parse as JSON object first
          try {
            const parsed = JSON.parse(attr);
            if (typeof parsed === "object") {
              // Merge with defaults, allowing partial overrides
              return { ...TOOLTIP_CONFIG.edgePadding, ...parsed };
            }
          } catch (e) {
            // Not JSON, try as single number
            const val = +attr;
            if (!isNaN(val)) {
              return { left: val, right: val, top: val, bottom: val };
            }
          }
        }
        return TOOLTIP_CONFIG.edgePadding;
      })(),
      delay: +el.dataset.tooltipDelay || TOOLTIP_CONFIG.delay,
      transitionDurationIn:
        +el.dataset.tooltipTransitionDurationIn ||
        TOOLTIP_CONFIG.transitionDurationIn,
      transitionDurationOut:
        +el.dataset.tooltipTransitionDurationOut ||
        TOOLTIP_CONFIG.transitionDurationOut,
      isInteractive:
        el.dataset.tooltipInteractive !== undefined
          ? el.dataset.tooltipInteractive === "" ||
            el.dataset.tooltipInteractive === "true"
          : TOOLTIP_CONFIG.isInteractive
    };
    new TooltipController(el, cfg);
  });
}

const observer = new MutationObserver(() => initTooltips());
observer.observe(document.body, { childList: true, subtree: true });
initTooltips();
